import { FunctionComponent, memo, useLayoutEffect, useMemo, useRef } from 'react';
import { Group, Vector3 } from 'three';
import { Jute } from '../..';
import { transitionManager } from '../../hooks';
import { useEnterOrExit, useIsSceneFlatImageTracked } from '../../hooks/general';
import { useDataStore, useSceneStore } from '../../zustand';
import { ISceneComp, ISpatialComponentUnion, ITransitionData, ITransitionEffectTypes, ITuple3 } from '../r3f-components/component-data-structure';
import FadeGroup from '../FadeGroup';
import { getOpacityTransitionRange, getSpatialTransitionRange, IEasingTypes, ITransCatTypes, Transition } from '../r3f-components/transitions';
import { ThreeElements, useFrame } from '@react-three/fiber';

const transitionAddedDict: {
	[id: string]: boolean;
} = {};

type IEntityLevelFadeGroupProps = ThreeElements['group'] & {
	sceneId: string;
	entityId: string;
	position: ITuple3;
	screenRelative: boolean;
};

const EntityLevelFadeGroup: FunctionComponent<IEntityLevelFadeGroupProps> = ({ children, sceneId, entityId: id, screenRelative, position, ...rest }) => {
	const fadeGroupRef = useRef<Group>(null);
	const transitionCategory = useSceneStore(({ transitionCategory }) => transitionCategory[sceneId]);
	const placementMode = useSceneStore(({ placementMode }) => placementMode);
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const entity = componentsById ? (componentsById[id] as ISpatialComponentUnion) : undefined;
	const scene = componentsById ? (componentsById[sceneId] as ISceneComp) : undefined;
	const enterOrExit = useEnterOrExit(transitionCategory);
	const parentSceneIsFlatImageTracked = useIsSceneFlatImageTracked(scene);

	const isTestMode = Jute.environment === 'test';
	const entitySpatialTransition = useMemo(
		() => new Transition<ITuple3>({ start: [0, 0, 0], end: [0, 0, 0], duration: 0, easingType: IEasingTypes.linear, delay: 0, isTestMode, transitionId: `spatial-${id}` }),
		[]
	); // eslint-disable-line react-hooks/exhaustive-deps
	const entityOpacityTransition = useMemo(() => new Transition<number>({ start: 0, end: 0, duration: 0, easingType: IEasingTypes.linear, delay: 0, isTestMode, transitionId: `opacity-${id}` }), []); // eslint-disable-line react-hooks/exhaustive-deps

	useFrame(() => {
		entityOpacityTransition.tick();
		entitySpatialTransition.tick();
	})

	// add spatial transition to transition manager (only once!)
	useLayoutEffect(() => {
		if (transitionAddedDict[id] || screenRelative) return;
		transitionManager[sceneId].add(entitySpatialTransition);
		transitionAddedDict[id] = true;
	}, [entitySpatialTransition, sceneId, id]);

	// use transition category to set opacity transition
	useLayoutEffect(() => {
		if (transitionCategory === ITransCatTypes.hidden || screenRelative || !entity || !scene) return;
		const { transitions } = entity;
		const noEntityExitTrans = !transitions?.[enterOrExit] && transitionCategory === ITransCatTypes.exit;
		const noSceneExitTrans = !scene.transitions?.[enterOrExit] && transitionCategory === ITransCatTypes.exit;
		const noEntityEnterTrans = !transitions?.[enterOrExit] && transitionCategory === ITransCatTypes.enter;
		if (noEntityExitTrans && !noSceneExitTrans) return; // don't let the entity disapear before scene transition finishes
		let { delay = 0, duration = 0 } = (transitions?.[enterOrExit] as ITransitionData) || {};
		let { to, from } = getOpacityTransitionRange(transitionCategory);
		if (noEntityEnterTrans || noEntityExitTrans) {
			to = 1;
			from = 1;
			duration = 0.2;
		}
		entityOpacityTransition.set({ to, from, delay, duration });
		entityOpacityTransition.start();
	}, [enterOrExit, entityOpacityTransition, entity, transitionCategory, id, fadeGroupRef, scene]); // eslint-disable-line react-hooks/exhaustive-deps

	// use transition category to set spatial transition
	useLayoutEffect(() => {
		if (transitionCategory === ITransCatTypes.hidden || screenRelative || !entity) return;

		const { transitions } = entity;
		let { type: effect = ITransitionEffectTypes.up, delay = 0, duration = 0 } = (transitions?.[enterOrExit] as ITransitionData) || {};
		if (!fadeGroupRef.current) return;
		const { x, y, z } = fadeGroupRef.current?.position as Vector3;
		const [sx, sy, sz] = transitionCategory === ITransCatTypes.exit ? [x, y, z] : position;

		// const [sx, sy, sz] = entity.position;
		const [ex, ey, ez] = position;
		let { to, from, threeProperty } = getSpatialTransitionRange({
			startPosition: [sx, sy, sz],
			enterEndPosition: [ex, ey, ez],
			effect,
			category: transitionCategory,
			positionOffset: 2,
			sceneIsFlatImageTracked: parentSceneIsFlatImageTracked
		});

		if (!transitions?.[enterOrExit]?.duration) {
			switch (threeProperty) {
				case 'position':
					from = [ex, ey, ez];
					to = [ex, ey, ez];
					break;
				case 'scale':
					from = [1, 1, 1];
					to = [1, 1, 1];
			}
		}

		// TODO: check why scale not affected and if fade out really needed
		// if scene has a fade transition after a spatial transition set the transition parameters to have no spatial effect
		if (effect === ITransitionEffectTypes.fadeIn || effect === ITransitionEffectTypes.fadeOut) {
			from = [ex, ey, ez];
			to = [ex, ey, ez];
			duration = 0;
		}

		// if the entity had a position or scale changing transition on exit and no position or scale changing
		// transition respectively on enter reset position/scale to the right values before enter
		if (transitionCategory === ITransCatTypes.enter) {
			switch (threeProperty) {
				case 'scale':
					fadeGroupRef.current?.position?.set?.(...(position as ITuple3));
					break;
				default:
					fadeGroupRef.current?.scale?.set?.(1, 1, 1);
					fadeGroupRef.current?.position?.set?.(...(position as ITuple3));
					break;
			}
		}

		entitySpatialTransition.set({ to, from, delay, duration });
		entitySpatialTransition.onChange((value) => {
			if (threeProperty === 'none') return;
			fadeGroupRef.current?.[threeProperty]?.set?.(...value);
		});
	}, [enterOrExit, entitySpatialTransition, transitionCategory, entity, fadeGroupRef, parentSceneIsFlatImageTracked]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{!!entity ? (
				<FadeGroup
					ref={fadeGroupRef}
					position={position}
					transition={entityOpacityTransition}
					visible={transitionCategory !== ITransCatTypes.hidden && !placementMode}
					transitionCategory={transitionCategory}
					screenRelativeEntityLevel={screenRelative}
					{...rest}
				>
					{children}
				</FadeGroup>
			) : null}
		</>
	);
};

EntityLevelFadeGroup.displayName = 'EntityLevelFadeGroup';
export default memo(EntityLevelFadeGroup);
